import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  CircularProgress,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridToolbar,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { S3Client, GetObjectCommand } from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import { Img } from 'react-image';
import Moment from 'moment';
import ImagePreviewModal from '../parts/ImagePreviewModal';
import { API_URL, UPLOADS_BUCKET } from '../../config';

// Retry helper for transient Lambda cold-start errors (5xx from API Gateway/Lambda)
async function fetchWithRetry(url: string, options: RequestInit, retries = 2, delay = 1500): Promise<Response> {
  for (let i = 0; i <= retries; i++) {
    const response = await fetch(url, options);
    // Retry on 500 (Lambda internal error), 502 (bad gateway), 503 (service unavailable)
    const isServerError = response.status === 500 || response.status === 502 || response.status === 503;
    if (!isServerError || i === retries) return response;
    await new Promise(resolve => setTimeout(resolve, delay));
  }
  throw new Error('fetchWithRetry exhausted'); // unreachable — TypeScript guard
}

// Shared status-to-color mapping used by both grid renderer and detail modal
function getStatusColor(status: string): "default" | "info" | "warning" | "error" | "success" {
    switch (status) {
        case 'Ready for Distribution': return 'warning';  // orange
        case 'Processing': return 'info';                   // blue
        case 'Failed': return 'error';                      // red
        case 'Empty': return 'default';                     // grey
        case 'SUCCESS_NO_OPTIMIZATION': return 'success';   // green (media only)
        default: return 'default';
    }
}

interface Collection {
  id: string;
  collectionName: string;
  createdAt: number;
  userId: string;
  collectionStatus: string;
  tier?: string;           // tier ID from API
  tierName?: string;       // resolved tier name for display
  photoCount?: number;
  lastModified?: number;
}

interface Media {
  id: string;
  fileName: string;
  photoStatus: string;
  src: string;
  key: string;
  status: string;
  originalPath?: string;
  optimizedPath?: string;
}

interface AdminDashboardV2Props {
  token: string;
  AWS: any;
}

const AdminDashboardV2: React.FC<AdminDashboardV2Props> = ({ token, AWS }) => {
  const navigate = useNavigate();
  
  // State for collections grid
  const [loading, setLoading] = useState(true);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });
  // Cursor history for DynamoDB pagination bridge
  const [cursorHistory, setCursorHistory] = useState<string[]>(['']);
  const [hasNextPage, setHasNextPage] = useState(false);
  
  // State for filters and search
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [tierFilter, setTierFilter] = useState('');
  const [tiers, setTiers] = useState<string[]>([]);
  const [tierMap, setTierMap] = useState<Record<string, string>>({});
  
  // State for details modal
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState<Collection | null>(null);
  const [modalMedia, setModalMedia] = useState<Media[]>([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [mediaExpanded, setMediaExpanded] = useState(false);
  const [mediaPagination, setMediaPagination] = useState<{
    nextCursor: string | null;
    hasNextPage: boolean;
    loadingMore: boolean;
  }>({ nextCursor: null, hasNextPage: false, loadingMore: false });
  
  // State for image preview
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [imagePreviewIndex, setImagePreviewIndex] = useState<number | null>(null);

  // Fetch collections with pagination and filters
  const fetchCollections = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const cursor = cursorHistory[paginationModel.page] || '';
      const params = new URLSearchParams({
        limit: paginationModel.pageSize.toString(),
        ...(cursor && { cursor }),
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter.length > 0 && { status: statusFilter.join(',') }),
        ...(tierFilter && { tier: tierFilter }),
      });

      const response = await fetchWithRetry(`${API_URL}/admin/v1/collections?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        
        // Build tier ID → name map from API response (allow id=0 for "No Tier")
        const newTierMap: Record<string, string> = {};
        if (Array.isArray(data.tierList)) {
          data.tierList.forEach((tier: any) => {
            const id = tier.id !== undefined ? tier.id : (tier.tierId !== undefined ? tier.tierId : tier.relId);
            const name = tier.tierName || tier.name;
            if (id !== undefined && id !== null && name !== undefined && name !== null) {
              newTierMap[String(id)] = name;
            }
          });
        }
        setTierMap(newTierMap);
        
        // Build lookup map from API response medialist for media counts
        const mediaCountByCollection: Record<string, number> = {};
        if (Array.isArray(data.medialist)) {
          data.medialist.forEach((m: any) => {
            mediaCountByCollection[m.photosetId] = m.photoCount || m.photos?.length || 0;
          });
        }

        // Enhance collections with resolved tier names and photo count from medialist
        const collectionsArray = Array.isArray(data.collections) ? data.collections : [];
        const enhancedCollections = collectionsArray.map((col: any) => ({
          ...col,
          collectionName: col.collectionName || col.photosetName || col.id || 'Untitled',
          collectionStatus: col.collectionStatus || col.photosetStatus || 'Empty',
          tierName: col.tier !== undefined && col.tier !== null ? (newTierMap[String(col.tier)] || String(col.tier)) : undefined,
          photoCount: mediaCountByCollection[col.id] || col.photoCount || 0,
        }));
        setCollections(enhancedCollections);
        
        setTotalCount(data.totalCount || 0);
        setHasNextPage(data.pagination?.hasNextPage || false);
        
        // Update cursor history when we get a new page
        const nextCursor = data.pagination?.nextCursor;
        if (nextCursor !== undefined && nextCursor !== null) {
          setCursorHistory(prev => {
            const next = [...prev];
            // Store cursor for the NEXT page at index currentPage + 1
            next[paginationModel.page + 1] = nextCursor;
            return next;
          });
        }
        
        // Extract unique tier names from the map for filter dropdown
        const uniqueTiers = Object.values(newTierMap);
        setTiers(uniqueTiers);
      } else {
        toast.error(`Failed to fetch collections: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching collections:', error);
      toast.error('Failed to fetch collections');
    } finally {
      setLoading(false);
    }
  // Note: cursorHistory is NOT in deps to avoid infinite loop.
  // We read cursorHistory[paginationModel.page] at call time.
  }, [token, paginationModel, searchTerm, statusFilter, tierFilter]);

  // Handle pagination model changes
  const handlePaginationModelChange = useCallback((newModel: GridPaginationModel) => {
    setPaginationModel(newModel);
  }, []);

  // Fetch collection details
  const fetchCollectionDetails = async (collectionId: string) => {
    if (!token) return;
    
    setModalLoading(true);
    try {
      const response = await fetchWithRetry(`${API_URL}/admin/v1/collections/${collectionId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedCollection(data);
        await fetchMedia(data.userId, collectionId);
      } else {
        toast.error(`Failed to fetch collection details: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching collection details:', error);
      toast.error('Failed to fetch collection details');
    } finally {
      setModalLoading(false);
    }
  };

  // Fetch media for a collection (supports cursor for pagination)
  const fetchMedia = async (userId: string, collectionId: string, cursor?: string) => {
    if (!token || !AWS?.config?.credentials) return;
    
    try {
      const params = new URLSearchParams({
        collectionId: collectionId,
        limit: '100',
        ...(cursor ? { cursor } : {}),
      });

      const response = await fetchWithRetry(`${API_URL}/admin/v1/media?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        
        // Generate signed URLs for thumbnails
        const s3 = new S3Client({
          region: 'us-east-1',
          credentials: AWS.config.credentials,
        });

        const mediaPromises = data.media.map(async (media: any) => {
          const fileNameWithoutExt = media.fileName.replace(/\.[^/.]+$/, '');
          const thumbnailFilename = `${fileNameWithoutExt}.jpg`;
          const key = `collections/${userId}/${collectionId}/thumbs/${thumbnailFilename}`;

          try {
            const command = new GetObjectCommand({
              Bucket: UPLOADS_BUCKET,
              Key: key,
            });
            const url = await getSignedUrl(s3, command, { expiresIn: 3600 });
            
            return {
              id: media.id,
              fileName: media.fileName,
              photoStatus: media.mediaStatus,
              status: media.mediaStatus,
              src: url,
              key: media.id,
              originalPath: `collections/${userId}/${collectionId}/${media.fileName}`,
              optimizedPath: `collections/${userId}/${collectionId}/optimized/${fileNameWithoutExt}.jpg`,
            };
          } catch (error) {
            console.warn(`Failed to get thumbnail for ${media.fileName}`);
            return null;
          }
        });

        const media = (await Promise.all(mediaPromises)).filter(Boolean);
        setModalMedia(prev => (cursor ? [...prev, ...media] : media));
        setMediaPagination(prev => ({
          ...prev,
          nextCursor: data.pagination?.nextCursor ?? null,
          hasNextPage: Boolean(data.pagination?.hasNextPage),
          loadingMore: false,
        }));
      } else {
        toast.error(`Failed to fetch media: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching media:', error);
      toast.error('Failed to fetch media');
    }
  };

  // Define columns for DataGrid
  const columns: GridColDef[] = [
    {
      field: 'collectionName',
      headerName: 'Collection Name',
      width: 250,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {params.value}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {params.row.id}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {Moment.unix(params.value).format('YYYY-MM-DD')}
        </Typography>
      ),
    },
    {
      field: 'userId',
      headerName: 'User',
      width: 200,
    },
    {
      field: 'tierName',
      headerName: 'Tier',
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        params.value ? (
          <Chip label={params.value} size="small" variant="outlined" />
        ) : (
          <Typography variant="body2" color="textSecondary">-</Typography>
        )
      ),
    },
    {
      field: 'collectionStatus',
      headerName: 'Status',
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.deletedAt) {
          return <Chip label="Deleted" size="small" color="error" />;
        }
        const status = params.value || 'Empty';
        const color = getStatusColor(status);
        return (
          <Chip
            label={status}
            size="small"
            color={color}
          />
        );
      },
    },
    {
      field: 'photoCount',
      headerName: 'Media',
      width: 100,
      type: 'number',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Button
          variant="outlined"
          size="small"
          startIcon={<VisibilityIcon />}
          onClick={() => handleDetailsClick(params.row)}
        >
          Details
        </Button>
      ),
    },
  ];

  // Event handlers
  const handleDetailsClick = (collection: Collection) => {
    setSelectedCollection(collection);
    setDetailsOpen(true);
    setMediaExpanded(false);
    setMediaPagination({ nextCursor: null, hasNextPage: false, loadingMore: false });
    fetchCollectionDetails(collection.id);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedCollection(null);
    setModalMedia([]);
    setMediaExpanded(false);
    setMediaPagination({ nextCursor: null, hasNextPage: false, loadingMore: false });
  };

  const handleImagePreview = (media: Media, index: number) => {
    setImagePreviewIndex(index);
    setImagePreviewOpen(true);
  };

  const handleMediaDeleted = (deletedMediaId: string) => {
    setModalMedia(prev => prev.filter(media => media.id !== deletedMediaId && media.key !== deletedMediaId));
    
    if (selectedCollection) {
      setMediaPagination({ nextCursor: null, hasNextPage: false, loadingMore: false });
      fetchCollectionDetails(selectedCollection.id);
    }
  };

  const handleLoadMoreMedia = async () => {
    if (!selectedCollection || !mediaPagination.hasNextPage || !mediaPagination.nextCursor || mediaPagination.loadingMore) return;
    setMediaPagination(prev => ({ ...prev, loadingMore: true }));
    await fetchMedia(selectedCollection.userId, selectedCollection.id, mediaPagination.nextCursor);
  };

  const handleDeleteCollection = async () => {
    if (!selectedCollection || !token) {
      toast.error("Unable to delete collection");
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the collection "${selectedCollection.collectionName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetchWithRetry(`${API_URL}/admin/v1/collections`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          collectionIds: [selectedCollection.id],
          userId: selectedCollection.userId
        }),
      });

      if (response.ok) {
        toast.success("Collection deleted successfully!");
        handleCloseDetails();
        fetchCollections();
      } else {
        const errorData = await response.json();
        toast.error(`Failed to delete collection: ${errorData.message || response.status}`);
      }
    } catch (error) {
      console.error("Error deleting collection:", error);
      toast.error("Error deleting collection: " + (error.message || error));
    }
  };

  const handleRefresh = () => {
    setCursorHistory(['']);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    // fetchCollections() fired automatically via useEffect on state change
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setCursorHistory(['']);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const handleStatusFilter = (status: string[]) => {
    setStatusFilter(status);
    setCursorHistory(['']);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const handleTierFilter = (tier: string) => {
    setTierFilter(tier);
    setCursorHistory(['']);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  // Effects
  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Admin Dashboard
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Manage collections and monitor system activity
        </Typography>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Search collections..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                multiple
                value={statusFilter}
                label="Status"
                onChange={(e) => handleStatusFilter(e.target.value as string[])}
              >
                <MenuItem value="Processing">Processing</MenuItem>
                <MenuItem value="Ready for Distribution">Ready for Distribution</MenuItem>
                <MenuItem value="Failed">Failed</MenuItem>
                <MenuItem value="Empty">Empty</MenuItem>
                <MenuItem value="Deleted">Deleted</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3} md={2}>
            <FormControl fullWidth>
              <InputLabel>Tier</InputLabel>
              <Select
                value={tierFilter}
                label="Tier"
                onChange={(e) => handleTierFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {Object.entries(tierMap).map(([id, name]) => (
                  <MenuItem key={id} value={id}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={loading}
              >
                Refresh
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Collections DataGrid */}
      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={collections}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[25, 50, 100]}
          rowCount={totalCount}
          paginationMode="server"
          loading={loading}
          slots={{
            toolbar: GridToolbar,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: false,
            },
          }}
          sx={{
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid #f0f0f0',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#fafafa',
            },
          }}
        />
      </Paper>

      {/* Details Modal */}
      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Collection Details
            </Typography>
            <IconButton onClick={handleCloseDetails}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {modalLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : selectedCollection ? (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Name</Typography>
                  <Typography variant="body1">{selectedCollection.collectionName}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                  <Chip
                    label={selectedCollection.collectionStatus}
                    size="small"
                    color={getStatusColor(selectedCollection.collectionStatus)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">User ID</Typography>
                  <Typography variant="body1">{selectedCollection.userId}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Created</Typography>
                  <Typography variant="body1">
                    {Moment.unix(selectedCollection.createdAt).format('YYYY-MM-DD HH:mm:ss')}
                  </Typography>
                </Grid>
                {selectedCollection.tierName && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">Tier</Typography>
                    <Typography variant="body1">{selectedCollection.tierName}</Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Collection ID</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {selectedCollection.id}
                  </Typography>
                </Grid>
              </Grid>

              {/* Media Section */}
              <Accordion expanded={mediaExpanded} onChange={() => setMediaExpanded(!mediaExpanded)}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">
                    Media ({modalMedia.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {modalMedia.length > 0 ? (
                    <>
                      <Grid container spacing={1}>
                        {modalMedia.map((media, index) => (
                        <Grid item xs={6} sm={4} md={3} lg={2} key={media.key}>
                          <Box
                            sx={{
                              position: 'relative',
                              cursor: 'pointer',
                              borderRadius: 1,
                              overflow: 'hidden',
                              '&:hover': {
                                transform: 'scale(1.02)',
                                transition: 'transform 0.2s',
                              },
                            }}
                            onClick={() => handleImagePreview(media, index)}
                          >
                            <Img
                              src={media.src}
                              loader={<CircularProgress size={20} />}
                              unloader={<CloseIcon />}
                              style={{
                                width: '100%',
                                height: 'auto',
                                aspectRatio: '1 / 1',
                                objectFit: 'cover',
                              }}
                            />
                            <Chip
                              label={media.status}
                              size="small"
                              sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              }}
                            />
                          </Box>
                        </Grid>
                        ))}
                      </Grid>
                      {mediaPagination.hasNextPage && (
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                          <Button
                            onClick={handleLoadMoreMedia}
                            disabled={mediaPagination.loadingMore}
                            variant="outlined"
                          >
                            {mediaPagination.loadingMore ? 'Loading...' : 'Load more'}
                          </Button>
                        </Box>
                      )}
                    </>
                  ) : (
                    <Typography color="textSecondary">No media found</Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleDeleteCollection}
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            Delete Collection
          </Button>
          <Button onClick={handleCloseDetails}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Modal */}
      {imagePreviewOpen && imagePreviewIndex !== null && selectedCollection && (
        <ImagePreviewModal
          open={imagePreviewOpen}
          onClose={() => setImagePreviewOpen(false)}
          media={modalMedia}
          initialIndex={imagePreviewIndex}
          collectionDetails={selectedCollection}
          awsCredentials={AWS?.config?.credentials}
          userId={selectedCollection.userId}
          collectionId={selectedCollection.id}
          versionTime={Date.now()}
          token={token}
          isAdmin={true}
          onMediaDeleted={handleMediaDeleted}
        />
      )}
    </Container>
  );
};

export default AdminDashboardV2;
